import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

// MUI Components
import TextField from "@mui/material/TextField";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";

// Context
import { useCoursesNewContext } from "../../context";

// Components
import DayPicker from "../../../../../components/DayPicker";
import CustomButton from "../../../../../components/CustomButton";

// Styles
import styles from "./style.module.scss";
import stepsStyles from "../steps.module.scss";

// Types
import { CourseData } from "../../types";

interface ScheduleProps {
  onNext: (courseData: CourseData | null) => void;
  onPrev: () => void;
}

const Schedule: React.FC<ScheduleProps> = ({ onNext, onPrev }) => {
  // Hooks
  const { t } = useTranslation();

  // Context
  const context = useCoursesNewContext();

  // State
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [rawStartDate, setRawStartDate] = useState<Dayjs | null>(null);
  const [rawEndDate, setRawEndDate] = useState<Dayjs | null>(null);
  const [errors, setErrors] = useState({
    startDate: false,
    endDate: false,
  });

  // Effects
  useEffect(() => {
    if (context.course?.start_date) {
      const startDate = context.course.start_date;
      setStartDate(startDate || null);
      setRawStartDate(dayjs(startDate));
    }

    if (context.course?.end_date) {
      setEndDate(context.course.end_date);
      setRawEndDate(dayjs(context.course.end_date));
    }
  }, [context.course?.start_date, context.course?.end_date]);

  // Handlers
  const handleChangeStartDate = useCallback((date: Dayjs) => {
    setStartDate(date.format('DD/MM/YYYY'));
    setRawStartDate(date);
    setErrors({ ...errors, startDate: false });
  }, []);

  const handleChangeEndDate = useCallback((date: Dayjs) => {
    setEndDate(date.format('DD/MM/YYYY'));
    setRawEndDate(date);
    setErrors({ ...errors, endDate: false });
  }, []);

  const handleNext = useCallback(async () => {
    const errors = {
      startDate: !startDate,
      endDate: !endDate,
    };

    if (errors.startDate || errors.endDate) {
      setErrors(errors);
      return;
    }

    const _course: CourseData = { ...context.course, start_date: startDate || undefined, end_date: endDate || undefined };

    if (context.course?.id) {
      const result = await context.updateCourse(_course);
      if (result.success) onNext(result.data);
    } else {
      onNext(_course);
    }
  }, [startDate, endDate, onNext]);

  const handlePrev = useCallback(() => {
    onPrev();
  }, [onPrev]);

  return (
    <div className={stepsStyles.root}>
      <div className={stepsStyles.content}>
        <h1 className='text-2xl font-bold text-black dark:text-white'>
          {t('courses.new.schedule.title')}
        </h1>

        <p className='mb-4 text-md text-gray-500 dark:text-gray-400'>
          {t('courses.new.schedule.subtitle')}
        </p>

        <div className={styles.form}>
          <div className={`${stepsStyles.formItem} ${stepsStyles.formItem_grow}`}>
            <label className={stepsStyles.label}>
              {t('courses.new.schedule.start_date_label')}
              <span className='text-blue-500'>*</span>
            </label>

            <TextField
              value={startDate}
              classes={{ root: styles.input }}
              slotProps={{
                input: {
                  readOnly: true,
                }
              }}
            />

            <DateCalendar
              value={rawStartDate}
              onChange={(date) => handleChangeStartDate(date as Dayjs)}
            />
          </div>

          <div className={`${stepsStyles.formItem} ${stepsStyles.formItem_grow}`}>
            <label className={stepsStyles.label}>
              {t('courses.new.schedule.end_date_label')}
              <span className='text-blue-500'>*</span>
            </label>

            <TextField
              value={endDate}
              classes={{ root: styles.input }}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
            />

            <DateCalendar
              value={rawEndDate}
              onChange={(date) => handleChangeEndDate(date as Dayjs)}
            />
          </div>
        </div>
      </div>

      <div className={stepsStyles.actions}>
        <CustomButton
          variant="secondary"
          onClick={handlePrev}
          classes={{ root: stepsStyles.prevButton }}
        >
          {t('actions.previous')}
        </CustomButton>

        <CustomButton
          variant="primary"
          onClick={handleNext}
          classes={{ root: stepsStyles.nextButton }}
          disabled={!startDate || !endDate}
        >
          {t('actions.next')}
        </CustomButton>
      </div>
    </div>
  );
};

export default Schedule;
